import { useEffect, useState } from 'react'

import { useLogger, useTenantMappings } from '@vp/ubik-context'

import { useAuthorization } from '../../root/contexts/AuthorizationProvider'
import { getSelectorApiResponse, type SelectorApiResponse } from '../../../clients/SelectorApiClient'

const PRODUCT_OPTIONS_QUANTITY_KEY = 'Quantity'

type QuantityRemainingOption = SelectorApiResponse['remainingOptions'][number]

type UseValidatedInitialSelectionsArgs = {
  productKey: string
  productVersion: number
  country: string
  merchandisingSelections: Record<string, string>
  initialQuantity: number | null | undefined
}

type UseValidatedInitialSelectionsResult = {
  initialSelections: Record<string, string> | undefined
  isResolved: boolean
}

const findQuantityOption = (remainingOptions: SelectorApiResponse['remainingOptions']): QuantityRemainingOption | undefined => {
  return remainingOptions.find((option) => option.key.toLowerCase() === PRODUCT_OPTIONS_QUANTITY_KEY.toLowerCase())
}

const isQuantityInRemainingOption = (quantity: number, quantityOption: QuantityRemainingOption): boolean => {
  return quantityOption.values.some((value) => {
    if (value.type === 'numberLiteral') {
      return Number(value.numberLiteral) === quantity
    }

    if (value.type !== 'range' || !value.range) {
      return false
    }

    const { minimum, maximum, increment } = value.range
    const isWithinRange = quantity >= minimum && (maximum === undefined || quantity <= maximum)
    if (!isWithinRange) {
      return false
    }
    if (!increment || increment <= 0) {
      return true
    }

    return (quantity - minimum) % increment === 0
  })
}

const getMinimumQuantityFromRemainingOption = (quantityOption: QuantityRemainingOption): number | undefined => {
  let minimumQuantity: number | undefined

  for (const value of quantityOption.values) {
    if (value.type === 'range' && value.range) {
      minimumQuantity = minimumQuantity === undefined ? value.range.minimum : Math.min(minimumQuantity, value.range.minimum)
    } else if (value.type === 'numberLiteral' && value.numberLiteral !== undefined) {
      minimumQuantity = minimumQuantity === undefined ? value.numberLiteral : Math.min(minimumQuantity, value.numberLiteral)
    }
  }

  return minimumQuantity
}

export const useValidatedInitialSelections = ({
  productKey,
  productVersion,
  country,
  merchandisingSelections,
  initialQuantity,
}: UseValidatedInitialSelectionsArgs): UseValidatedInitialSelectionsResult => {
  const { warn } = useLogger()
  const { pim: merchantId } = useTenantMappings()
  const { authorizationHeader } = useAuthorization()

  const [initialSelections, setInitialSelections] = useState<Record<string, string> | undefined>()
  const [isResolved, setIsResolved] = useState(false)

  useEffect(() => {
    let isDisposed = false

    const resolveInitialSelections = async () => {
      const selectionsWithoutQuantity = { ...merchandisingSelections }
      const fallbackSelections: Record<string, string> = { ...selectionsWithoutQuantity }
      const hasInitialQuantity = Number.isFinite(initialQuantity) && Number(initialQuantity) > 0

      if (!hasInitialQuantity) {
        if (!isDisposed) {
          setInitialSelections(Object.keys(fallbackSelections).length > 0 ? fallbackSelections : undefined)
          setIsResolved(true)
        }
        return
      }

      const quantityToValidate = Number(initialQuantity)
      fallbackSelections[PRODUCT_OPTIONS_QUANTITY_KEY] = String(quantityToValidate)

      if (!authorizationHeader || !merchantId || !country) {
        if (!isDisposed) {
          setInitialSelections(fallbackSelections)
          setIsResolved(true)
        }
        return
      }

      try {
        const response = await getSelectorApiResponse({
          productKey,
          productVersion,
          merchant: merchantId,
          country,
          selections: selectionsWithoutQuantity,
          authHeader: authorizationHeader,
        })

        const quantityOption = findQuantityOption(response.remainingOptions)
        if (!quantityOption) {
          if (!isDisposed) {
            setInitialSelections(fallbackSelections)
            setIsResolved(true)
          }
          return
        }

        if (isQuantityInRemainingOption(quantityToValidate, quantityOption)) {
          if (!isDisposed) {
            setInitialSelections(fallbackSelections)
            setIsResolved(true)
          }
          return
        }

        const minimumQuantity = getMinimumQuantityFromRemainingOption(quantityOption)
        if (!isDisposed) {
          setInitialSelections({
            ...selectionsWithoutQuantity,
            [PRODUCT_OPTIONS_QUANTITY_KEY]: String(minimumQuantity ?? quantityToValidate),
          })
          setIsResolved(true)
        }
      } catch (error) {
        warn('Unable to validate initial quantity with Selector API', {
          productKey,
          productVersion,
          country,
          merchantId,
          initialQuantity: quantityToValidate,
          error: error as Error,
        })
        if (!isDisposed) {
          setInitialSelections(fallbackSelections)
          setIsResolved(true)
        }
      }
    }

    setIsResolved(false)
    void resolveInitialSelections()

    return () => {
      isDisposed = true
    }
  }, [authorizationHeader, country, initialQuantity, merchantId, merchandisingSelections, productKey, productVersion, warn])

  return { initialSelections, isResolved }
}
